import { searchClasses } from "@/services/naaviApi/classesService";
import { extractTenantId } from "@/helpers/tenantHelper";
import defaultClassImage from "@/assets/images/healthcare-image1.png";
import classArrowLeft from "@/assets/images/class-arrow-left.svg";
import classArrowRight from "@/assets/images/class-arrow-right.svg";
import AppButton from "@/components/buttons/AppButton.vue";
import ClassSection from "@/components/classes/ClassSection.vue";
import LoaderOverlay from "@/components/loader/loader.vue";
import MainContent from "@/components/design/mainContent.vue";
import SubContent from "@/components/design/subContent.vue";
import { resolvePriceLabel } from "@/helpers/assetHelper";
import { normalizeClassPrice } from "@/helpers/classPriceHelper";
import { formatScheduleDateParts } from "@/helpers/dateLabelHelper";
import {
  DateHelper,
  extractDatePart,
  toStartOfDayIso as toLocalStartOfDayIso,
} from "@/helpers/dateHelper";

export default {
  name: "ClassView",
  components: {
    AppButton,
    ClassSection,
    LoaderOverlay,
    MainContent,
    SubContent,
  },
  data() {
    return {
      classItem: null,
      loading: true,
      loadError: null,
      tenantClasses: [],
      selectedDate: null,
      calendarStartDate: null,
      availabilityLoading: false,
      availabilityError: null,
      classArrowLeft,
      classArrowRight,
    };
  },
  async mounted() {
    await this.loadClass();
  },
  watch: {
    "$route.params.date"(newValue) {
      const iso = extractDatePart(newValue);
      if (!iso || iso === this.selectedDate) {
        return;
      }
      this.handleDateChange(iso);
    },
    "$route.params.therapistId"(newValue, oldValue) {
      if (newValue === oldValue) {
        return;
      }
      this.loadClass();
    },
    "$route.params.tenantId"(newValue, oldValue) {
      if (newValue === oldValue) {
        return;
      }
      this.loadClass();
    },
  },
  computed: {
    calendarItems() {
      return this.generateCalendarItems();
    },
    formattedDate() {
      return this.resolveClassFormattedDate(this.classItem);
    },
    therapistProfileRoute() {
      return this.resolveTherapistProfileRouteForClass(this.classItem);
    },
    therapistAvatar() {
      return this.resolveTherapistAvatarForClass(this.classItem);
    },
    classSections() {
      if (!Array.isArray(this.tenantClasses) || !this.tenantClasses.length) {
        return [];
      }
      return this.tenantClasses.map((classData, index) => ({
        classData,
        scheduleCards: this.buildScheduleCardsForClass(classData),
        headline: this.resolveClassHeadline(classData),
        summaryText: this.resolveClassSummaryText(classData),
        highlights: this.resolveClassHighlights(classData),
        location: this.resolveClassLocation(classData),
        priceLabel: this.resolvePriceLabelForClass(classData),
        mediaGallery: this.resolveMediaGallery(classData),
        key: `${classData?.classId ?? "class"}-${index}`,
      }));
    },
    noClassesAvailable() {
      if (!this.classSections.length) {
        return true;
      }
      return this.classSections.every(
        (section) =>
          !section.scheduleCards || section.scheduleCards.length === 0,
      );
    },
  },
  methods: {
    async loadClass() {
      try {
        this.loading = true;
        this.loadError = null;

        const initialDate = this.resolveInitialDate();
        this.selectedDate = initialDate;
        this.calendarStartDate = this.computeCalendarStart(initialDate);

        await this.loadClassesForDate(initialDate, {
          tenantId: this.$route.params?.tenantId ?? null,
          therapistId: this.$route.params?.therapistId ?? null,
        });

        if (!this.classItem && this.tenantClasses.length) {
          this.classItem = this.tenantClasses[0];
        }

        this.availabilityLoading = false;
        this.availabilityError = null;
      } catch (error) {
        this.loadError = error;
        this.classItem = null;
        this.tenantClasses = [];
        this.selectedDate = null;
      } finally {
        this.loading = false;
      }
    },
    async loadClassesForDate(dateIso, context = {}) {
      const normalizedDate =
        dateIso ||
        this.selectedDate ||
        extractDatePart(toLocalStartOfDayIso(new Date()));
      this.availabilityLoading = true;

      try {
        const payload = this.buildClassSearchPayload(
          {
            date: normalizedDate,
            tenantId: context.tenantId ?? this.$route.params?.tenantId ?? null,
          },
          context,
        );

        const response = await searchClasses(payload);
        const classes = Array.isArray(response?.items) ? response.items : [];
        const normalizedClasses = classes.map((item) =>
          normalizeClassPrice(item),
        );

        const therapistId =
          context.therapistId ?? this.resolveTherapistId(context);
        const filteredClasses = this.filterClassesByContext(normalizedClasses, {
          therapistId,
        });
        const prioritizedClasses = filteredClasses.length
          ? filteredClasses
          : normalizedClasses;

        const groupedClasses =
          this.groupClassesByBookingType(prioritizedClasses);

        this.tenantClasses = groupedClasses;
        this.availabilityError = null;

        if (!groupedClasses.length) {
          this.classItem = null;
          return [];
        }

        const firstWithBookings = groupedClasses.find(
          (item) => this.getBookingsForDate(item, normalizedDate).length > 0,
        );
        const targetClass =
          firstWithBookings ||
          this.pickClassForDate(groupedClasses, normalizedDate) ||
          groupedClasses[0];

        this.classItem = targetClass;
        return groupedClasses;
      } catch (error) {
        this.tenantClasses = [];
        this.classItem = null;
        this.availabilityError = error;
        return [];
      } finally {
        this.availabilityLoading = false;
      }
    },
    extractQueryValue(key) {
      const raw = this.$route?.query?.[key];
      if (Array.isArray(raw)) {
        return raw.length ? String(raw[0]) : null;
      }
      if (raw === undefined || raw === null || raw === "") {
        return null;
      }
      return String(raw);
    },
    resolveTherapistId(context = null) {
      const param = this.$route?.params?.therapistId;
      if (param !== undefined && param !== null && param !== "") {
        return String(param);
      }

      const fromQuery = this.extractQueryValue("therapistId");
      if (fromQuery) {
        return fromQuery;
      }

      const source = context || this.classItem || {};
      const therapistCandidate = source?.therapist?.id;
      if (
        therapistCandidate !== undefined &&
        therapistCandidate !== null &&
        therapistCandidate !== ""
      ) {
        return String(therapistCandidate);
      }

      const slotResource =
        Array.isArray(source?.bookings) && source.bookings.length
          ? source.bookings[0]?.resourceId
          : null;

      if (
        slotResource !== undefined &&
        slotResource !== null &&
        slotResource !== ""
      ) {
        return String(slotResource);
      }

      return null;
    },
    resolveTherapyParam(context = null) {
      const fromQuery = this.extractQueryValue("therapy");
      if (fromQuery) {
        return fromQuery;
      }
      const source = context || this.classItem;
      if (source?.therapy) {
        return source.therapy;
      }
      return null;
    },
    resolveTherapyTypeParam(context = null) {
      const fromQuery = this.extractQueryValue("therapyType");
      if (fromQuery) {
        return fromQuery;
      }
      const source = context || this.classItem;
      if (source?.therapyType) {
        return source.therapyType;
      }
      return null;
    },
    buildClassSearchPayload(options = {}, context = null) {
      const { date = null, tenantId = null } = options;
      const effectiveContext = context || this.classItem || {};

      const payload = {
        date: this.toStartOfDayIso(
          date || extractDatePart(effectiveContext.start),
        ),
        maxResults: 200,
      };

      const tenantCandidates = new Set();
      const addTenant = (value) => {
        if (!value) {
          return;
        }
        const candidate = String(value);
        if (!candidate.length) {
          return;
        }
        tenantCandidates.add(candidate);
        const canonical = extractTenantId(candidate);
        if (canonical) {
          tenantCandidates.add(canonical);
        }
      };

      addTenant(tenantId);
      addTenant(effectiveContext.tenantId);
      addTenant(this.$route.params?.tenantId);
      addTenant(this.extractQueryValue("tenantIgniteId"));

      if (tenantCandidates.size) {
        payload.tenantIds = Array.from(tenantCandidates);
      }

      const therapy = this.resolveTherapyParam(effectiveContext);
      if (therapy) {
        payload.therapy = therapy;
      }

      const therapyType = this.resolveTherapyTypeParam(effectiveContext);
      if (therapyType) {
        payload.therapyType = therapyType;
      }

      if (typeof effectiveContext.online === "boolean") {
        payload.online = effectiveContext.online;
      }

      return payload;
    },
    filterClassesByContext(classes, { therapistId } = {}) {
      if (!Array.isArray(classes)) {
        return [];
      }

      let results = classes;
      if (therapistId) {
        const matches = classes.filter((item) =>
          this.matchTherapist(item, therapistId),
        );
        if (matches.length) {
          results = matches;
        }
      }

      return results;
    },
    matchTherapist(classItem, therapistId) {
      if (!classItem) {
        return false;
      }

      const candidate =
        classItem?.therapist?.id ?? classItem?.bookings?.[0]?.resourceId;
      if (candidate === undefined || candidate === null || candidate === "") {
        return false;
      }

      return String(candidate) === String(therapistId);
    },
    scrollToSchedule() {
      const target = this.$refs?.scheduleSection;
      if (!target) {
        return;
      }

      const element = target.$el ? target.$el : target;
      if (element && typeof element.scrollIntoView === "function") {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    },
    computeCalendarStart(centerIso) {
      const base = centerIso ? new Date(centerIso) : new Date();
      const start = new Date(base);
      start.setDate(base.getDate() - 5);
      return extractDatePart(this.toStartOfDayIso(start));
    },
    generateCalendarItems() {
      const items = [];
      const startBase = this.calendarStartDate
        ? new Date(this.calendarStartDate)
        : new Date();
      const locale =
        this.$i18n && typeof this.$i18n.locale === "string"
          ? this.$i18n.locale
          : "es-ES";
      const translate =
        typeof this.$t === "function" ? this.$t.bind(this) : null;
      for (let i = 0; i < 11; i += 1) {
        const date = new Date(startBase);
        date.setDate(startBase.getDate() + i);
        const iso = extractDatePart(this.toStartOfDayIso(date));
        const parts = formatScheduleDateParts(date, locale, translate);
        items.push({
          iso,
          month: parts.month,
          day: parts.day,
          weekday: parts.weekday,
          selected: iso === this.selectedDate,
        });
      }
      return items;
    },
    isDateHiddenOnMobile(index, length) {
      if (typeof index !== "number" || typeof length !== "number") {
        return false;
      }
      if (length <= 3) {
        return false;
      }
      return index < 4 || index >= length - 4;
    },
    pickClassForDate(classes, dateIso) {
      if (!Array.isArray(classes) || !classes.length) {
        return null;
      }

      const targetDate =
        dateIso || this.selectedDate || extractDatePart(this.classItem?.start);
      let candidates = classes;

      if (targetDate) {
        const sameDay = classes.filter(
          (item) => extractDatePart(item.start) === targetDate,
        );
        if (!sameDay.length) {
          return null;
        }
        candidates = sameDay;
      }

      const currentClassId = this.classItem?.classId;
      if (currentClassId) {
        const matchById = candidates.find(
          (item) => Number(item.classId) === Number(currentClassId),
        );
        if (matchById) {
          return matchById;
        }
      }

      const therapistId = this.classItem?.therapist?.id;
      if (therapistId) {
        const therapistMatches = candidates.filter(
          (item) => Number(item?.therapist?.id) === Number(therapistId),
        );
        if (therapistMatches.length) {
          candidates = therapistMatches;
        }
      }

      const bookingTypeId = this.classItem?.bookingTypeId;
      if (bookingTypeId) {
        const bookingMatches = candidates.filter(
          (item) => Number(item?.bookingTypeId) === Number(bookingTypeId),
        );
        if (bookingMatches.length) {
          candidates = bookingMatches;
        }
      }

      return candidates[0] ?? null;
    },
    toStartOfDayIso(dateIso) {
      return toLocalStartOfDayIso(dateIso || new Date());
    },
    groupClassesByBookingType(classes) {
      if (!Array.isArray(classes) || !classes.length) {
        return [];
      }

      const groups = new Map();

      classes.forEach((item) => {
        if (!item || typeof item !== "object") {
          return;
        }

        const key =
          item.bookingTypeId ??
          item.classId ??
          `class-${item.tenantId ?? "unknown"}`;
        const existing = groups.get(key);
        const clonedBookings = this.mergeBookingCollections(
          [],
          Array.isArray(item.bookings) ? item.bookings : [],
        );

        if (!existing) {
          groups.set(key, {
            ...item,
            bookings: clonedBookings,
          });
          return;
        }

        const merged = {
          ...existing,
          classId: existing.classId ?? item.classId,
          description: existing.description || item.description || "",
          extendedDescription:
            existing.extendedDescription || item.extendedDescription || "",
          imageUrl: existing.imageUrl || item.imageUrl || existing.imageUrl,
          therapist: existing.therapist || item.therapist,
          bookings: this.mergeBookingCollections(
            existing.bookings,
            clonedBookings,
          ),
          start: this.pickEarlier(existing.start, item.start),
          end: this.pickLater(existing.end, item.end),
          participantsIn: existing.participantsIn ?? item.participantsIn,
          maxParticipants: existing.maxParticipants ?? item.maxParticipants,
          hasBooking: existing.hasBooking || item.hasBooking,
        };

        if (!merged.price && item.price) {
          merged.price = item.price;
        }

        groups.set(key, merged);
      });

      return Array.from(groups.values()).map((group) => {
        const sortedBookings = this.mergeBookingCollections(
          [],
          group.bookings,
        ).sort((a, b) => {
          const startA = new Date(a.start || 0).getTime();
          const startB = new Date(b.start || 0).getTime();
          return startA - startB;
        });

        const normalized = {
          ...group,
          bookings: sortedBookings,
        };

        if (sortedBookings.length) {
          normalized.start = sortedBookings[0].start ?? normalized.start;
          normalized.end =
            sortedBookings[sortedBookings.length - 1].end ?? normalized.end;
        }

        return normalized;
      });
    },
    mergeBookingCollections(base, incoming) {
      const collection = Array.isArray(base)
        ? base.map((booking) => ({ ...booking }))
        : [];
      const knownKeys = new Set(
        collection.map((booking) => this.resolveBookingKey(booking)),
      );

      incoming.forEach((booking) => {
        if (!booking || typeof booking !== "object") {
          return;
        }
        const key = this.resolveBookingKey(booking);
        if (knownKeys.has(key)) {
          return;
        }
        knownKeys.add(key);
        collection.push({ ...booking });
      });

      return collection;
    },
    resolveBookingKey(booking) {
      if (!booking || typeof booking !== "object") {
        return Math.random().toString(36).slice(2);
      }
      if (booking.id !== undefined && booking.id !== null) {
        return String(booking.id);
      }
      const start = booking.start ? new Date(booking.start).getTime() : "";
      const resource = booking.resourceId ?? "";
      return `${start}-${resource}`;
    },
    pickEarlier(current, candidate) {
      if (!candidate) {
        return current ?? null;
      }
      if (!current) {
        return candidate;
      }
      const currentTime = new Date(current).getTime();
      const candidateTime = new Date(candidate).getTime();
      if (Number.isNaN(currentTime) || Number.isNaN(candidateTime)) {
        return current;
      }
      return candidateTime < currentTime ? candidate : current;
    },
    pickLater(current, candidate) {
      if (!candidate) {
        return current ?? null;
      }
      if (!current) {
        return candidate;
      }
      const currentTime = new Date(current).getTime();
      const candidateTime = new Date(candidate).getTime();
      if (Number.isNaN(currentTime) || Number.isNaN(candidateTime)) {
        return current;
      }
      return candidateTime > currentTime ? candidate : current;
    },
    prevDates() {
      if (!this.calendarStartDate) {
        return;
      }
      const start = new Date(this.calendarStartDate);
      start.setDate(start.getDate() - 7);
      this.calendarStartDate = extractDatePart(this.toStartOfDayIso(start));
    },
    nextDates() {
      if (!this.calendarStartDate) {
        return;
      }
      const start = new Date(this.calendarStartDate);
      start.setDate(start.getDate() + 7);
      this.calendarStartDate = extractDatePart(this.toStartOfDayIso(start));
    },
    resolveSlotParticipants(classSlotMeta, context) {
      const normalize = (value) => {
        if (value === null || value === undefined) {
          return null;
        }
        const numeric = Number(value);
        if (!Number.isFinite(numeric) || numeric < 0) {
          return null;
        }
        return Math.round(numeric);
      };

      const candidates = [];
      if (classSlotMeta) {
        candidates.push(
          classSlotMeta.participantsIn,
          classSlotMeta.participants,
          classSlotMeta.booked,
          classSlotMeta.occupied,
          classSlotMeta.enrolled,
          classSlotMeta.currentParticipants,
        );
      }

      candidates.push(
        context?.participantsIn,
        context?.participants,
        context?.booked,
      );

      for (const candidate of candidates) {
        const normalized = normalize(candidate);
        if (normalized !== null) {
          return normalized;
        }
      }

      return null;
    },
    resolveSlotCapacity(classSlotMeta, context, participantsIn) {
      const normalize = (value) => {
        if (value === null || value === undefined) {
          return null;
        }
        const numeric = Number(value);
        if (!Number.isFinite(numeric) || numeric <= 0) {
          return null;
        }
        return Math.round(numeric);
      };

      const totalCandidates = [];
      if (classSlotMeta) {
        totalCandidates.push(
          classSlotMeta.maxParticipants,
          classSlotMeta.capacity,
          classSlotMeta.maxCapacity,
          classSlotMeta.maximumCapacity,
        );

        const remaining = normalize(
          classSlotMeta.availableSpots ??
            classSlotMeta.available ??
            classSlotMeta.remaining ??
            classSlotMeta.remainingCapacity ??
            classSlotMeta.capacityLeft,
        );

        if (remaining !== null) {
          const current =
            this.resolveSlotParticipants(classSlotMeta, context) ??
            participantsIn ??
            0;
          const computedTotal = normalize(current + remaining);
          if (computedTotal !== null) {
            totalCandidates.unshift(computedTotal);
          }
        }
      }

      totalCandidates.push(context?.maxParticipants, context?.capacity);

      for (const candidate of totalCandidates) {
        const normalized = normalize(candidate);
        if (normalized !== null) {
          return normalized;
        }
      }

      return null;
    },
    formatSlotParticipants(current, total) {
      const normalize = (value) => {
        if (value === null || value === undefined) {
          return null;
        }
        const numeric = Number(value);
        if (!Number.isFinite(numeric) || numeric < 0) {
          return null;
        }
        return Math.round(numeric);
      };

      const normalizedCurrent = normalize(current);
      const normalizedTotal = normalize(total);

      if (normalizedCurrent === null && normalizedTotal === null) {
        return null;
      }

      if (normalizedTotal !== null) {
        return this.$t("Participants slot", {
          current: normalizedCurrent ?? 0,
          total: normalizedTotal,
        });
      }

      if (normalizedCurrent !== null) {
        return this.$t("Participants current slot", {
          current: normalizedCurrent,
        });
      }

      return null;
    },
    async setSelectedDate(dateIso) {
      await this.handleDateChange(dateIso, true);
    },
    async handleDateChange(dateIso, shouldUpdateRoute = false) {
      if (!dateIso) {
        return;
      }

      this.selectedDate = dateIso;
      this.calendarStartDate = this.computeCalendarStart(dateIso);
      await this.loadClassesForDate(dateIso);

      if (shouldUpdateRoute) {
        this.updateRouteDate(dateIso);
      }
    },
    updateRouteDate(dateIso) {
      const query = { ...this.$route.query };
      if (dateIso) {
        query.dateFrom = `${dateIso}T00:00:00`;
      } else {
        delete query.dateFrom;
      }

      const params = {};
      const currentParams = this.$route.params || {};
      if (currentParams.tenantId) {
        params.tenantId = String(currentParams.tenantId);
      }
      if (currentParams.therapistId) {
        params.therapistId = String(currentParams.therapistId);
      }
      if (dateIso) {
        params.date = dateIso;
      }

      this.$router
        .replace({
          name: this.$route.name || "ClassDetail",
          params,
          query,
        })
        .catch((error) => {
          void error;
        });
    },
    resolveInitialDate() {
      const routeDate = this.$route?.params?.date;
      const queryDate = this.extractQueryValue("dateFrom");
      const candidates = [routeDate, queryDate, this.classItem?.start];
      for (const candidate of candidates) {
        const normalized = extractDatePart(candidate);
        if (normalized) {
          return normalized;
        }
      }
      return extractDatePart(this.toStartOfDayIso(new Date())) || "";
    },
    resolveClassHeadline(classData) {
      if (!classData) {
        return "";
      }
      return classData.description || classData.extendedDescription || "";
    },
    resolveClassSummaryText(classData) {
      if (!classData) {
        return "";
      }
      if (classData.extendedDescription) {
        return classData.extendedDescription;
      }
      const headline = this.resolveClassHeadline(classData);
      if (classData.description && classData.description !== headline) {
        return classData.description;
      }
      return "";
    },
    resolveClassHighlights(classData) {
      if (!classData) {
        return [];
      }

      const candidates = [
        classData.highlights,
        classData.benefits,
        classData.features,
        classData.keyPoints,
        classData.tags,
        classData.bullets,
      ];

      for (const candidate of candidates) {
        if (Array.isArray(candidate)) {
          return candidate
            .map((item) => {
              if (typeof item === "string") {
                return item.trim();
              }
              if (item && typeof item === "object") {
                const label = item.label || item.name || item.title;
                if (typeof label === "string") {
                  return label.trim();
                }
              }
              return "";
            })
            .filter(Boolean)
            .slice(0, 4);
        }
        if (typeof candidate === "string") {
          const parsed = candidate
            .split(/[\n;,|]+/)
            .map((item) => item.trim())
            .filter(Boolean)
            .slice(0, 4);
          if (parsed.length) {
            return parsed;
          }
        }
      }

      return [];
    },
    resolveClassFormattedDate(classData) {
      if (!classData) {
        return "";
      }
      return classData.startLocalDisplay;
    },
    resolveMediaGallery(classData) {
      const media = [];
      if (classData?.imageUrl) {
        media.push(classData.imageUrl);
      }
      if (Array.isArray(classData?.media)) {
        classData.media.forEach((item) => {
          if (item?.url) {
            media.push(item.url);
          }
        });
      }
      if (!media.length) {
        media.push(defaultClassImage);
      }
      while (media.length < 3) {
        media.push(media[0]);
      }
      return media.slice(0, 5);
    },
    resolveClassLocation(classData) {
      if (!classData) {
        return null;
      }

      const locationName =
        typeof classData.locationName === "string"
          ? classData.locationName.trim()
          : "";
      const fullAddress =
        typeof classData.fullAddress === "string"
          ? classData.fullAddress.trim()
          : "";

      if (!locationName && !fullAddress) {
        return null;
      }

      const addressLines = fullAddress
        ? fullAddress
            .split(",")
            .map((line) => line.trim())
            .filter(Boolean)
        : [];

      const displayAddress = addressLines.length
        ? addressLines.join(", ")
        : fullAddress;

      return {
        name: locationName,
        addressLines,
        fullAddress: displayAddress,
      };
    },
    resolvePriceLabelForClass(classData) {
      if (!classData) {
        return null;
      }
      return (
        resolvePriceLabel(
          classData.price ?? null,
          classData.priceLabel ?? null,
        ) ||
        classData.priceLabel ||
        null
      );
    },
    resolveTherapistProfileRouteForClass() {
      return null;
    },
    resolveTherapistAvatarForClass(classData) {
      const raw = classData?.therapist?.avatarUrl || "";
      const normalized = typeof raw === "string" ? raw.trim() : "";
      return normalized;
    },
    getBookingsForDate(classData, dateIso) {
      const bookings = Array.isArray(classData?.bookings)
        ? classData.bookings
        : [];
      if (!dateIso) {
        return bookings;
      }
      return bookings.filter(
        (booking) => extractDatePart(booking?.start) === dateIso,
      );
    },
    resolveBookingDurationMinutes(booking, classData) {
      if (booking?.durationMinutes && Number(booking.durationMinutes) > 0) {
        return Math.round(Number(booking.durationMinutes));
      }
      if (booking?.duration && Number(booking.duration) > 0) {
        return Math.round(Number(booking.duration));
      }

      const start = booking?.start;
      const end = booking?.end;
      if (start && end) {
        const startDate = new Date(start);
        const endDate = new Date(end);
        const diff = (endDate.getTime() - startDate.getTime()) / 60000;
        if (Number.isFinite(diff) && diff > 0) {
          return Math.round(diff);
        }
      }

      if (classData?.durationMinutes && Number(classData.durationMinutes) > 0) {
        return Math.round(Number(classData.durationMinutes));
      }

      return null;
    },
    buildScheduleCardsForClass(classData, dateIso = this.selectedDate) {
      if (!classData) {
        return [];
      }

      const bookings = this.getBookingsForDate(classData, dateIso);
      if (!bookings.length) {
        return [];
      }

      const basePriceLabel = this.resolvePriceLabelForClass(classData);

      return bookings.map((booking, index) => {
        const start = booking?.start || classData.start || null;
        const durationMinutes = this.resolveBookingDurationMinutes(
          booking,
          classData,
        );
        let end = booking?.end || classData.end || null;
        if (
          (!end || Number.isNaN(new Date(end).getTime())) &&
          start &&
          durationMinutes
        ) {
          const startDate = new Date(start);
          if (!Number.isNaN(startDate.getTime())) {
            end = DateHelper.toSystemString(
              new Date(startDate.getTime() + durationMinutes * 60000),
            );
          }
        }

        const startLocalDisplay = booking?.startLocalDisplay;

        const endLocalDisplay = booking?.endLocalDisplay;

        const participantsIn = this.resolveSlotParticipants(booking, classData);
        const maxParticipants = this.resolveSlotCapacity(
          booking,
          classData,
          participantsIn,
        );

        const bookingClone = { ...booking };
        if (start) {
          bookingClone.start = start;
        }
        if (end) {
          bookingClone.end = end;
        }
        bookingClone.startLocalDisplay = startLocalDisplay;
        if (endLocalDisplay) {
          bookingClone.endLocalDisplay = endLocalDisplay;
        }
        if (durationMinutes !== null) {
          bookingClone.durationMinutes = durationMinutes;
        }
        if (participantsIn !== null) {
          bookingClone.participantsIn = participantsIn;
        }
        if (maxParticipants !== null) {
          bookingClone.maxParticipants = maxParticipants;
        }

        const payload = {
          ...bookingClone,
          duration:
            bookingClone.duration ??
            bookingClone.durationMinutes ??
            durationMinutes,
          expectedGrossAmount: classData?.price?.grossAmount ?? null,
          expectedPriceLabel: basePriceLabel ?? null,
        };

        if (!("bookingId" in payload) || payload.bookingId == null) {
          payload.bookingId = bookingClone.bookingId ?? bookingClone.id ?? null;
        }

        const cardData = {
          ...classData,
          start,
          end,
          startLocalDisplay,
          endLocalDisplay,
          durationMinutes,
          priceLabel: basePriceLabel ?? classData?.priceLabel ?? null,
          bookings: [payload],
          participantsIn,
          maxParticipants,
          slots: [payload],
        };

        if (!("bookingId" in cardData) || cardData.bookingId == null) {
          cardData.bookingId = payload.bookingId ?? null;
        }

        return {
          id: `${classData.classId ?? "class"}-${
            booking?.id ?? booking?.start ?? index
          }`,
          cardData,
          payload,
          startLabel: startLocalDisplay,
          priceLabel: basePriceLabel,
        };
      });
    },
  },
};
